import type {
  AudienceSegment,
  AudienceSnapshot,
  CampaignStatus,
  RecipientStatus,
  RecipientsPage,
  SendErrorRow,
  SendMode,
  SendPoint,
} from '../model/types';
import type {
  ApiAudienceOption,
  ApiAudienceSnapshot,
  ApiRecipientsPage,
  ApiSendError,
  ApiSendPoint,
} from './types';

/**
 * Mapeadores do vocabulário comum. Tolerantes de propósito: campo que a API
 * não mandou vira o neutro da tela (zero, vazio, `draft`), nunca `undefined`
 * em lugar que a tela lê sem checar.
 */

const CAMPAIGN_STATUSES: readonly CampaignStatus[] = ['draft', 'scheduled', 'sending', 'sent', 'paused', 'failed', 'cancelled'];
const RECIPIENT_STATUSES: readonly RecipientStatus[] = ['queued', 'delivered', 'opened', 'clicked', 'rejected', 'unsubscribed'];

export function asCampaignStatus(value: unknown): CampaignStatus {
  return CAMPAIGN_STATUSES.includes(value as CampaignStatus) ? (value as CampaignStatus) : 'draft';
}

export function asRecipientStatus(value: unknown): RecipientStatus {
  return RECIPIENT_STATUSES.includes(value as RecipientStatus) ? (value as RecipientStatus) : 'queued';
}

export function asSendMode(value: unknown): SendMode | null {
  return value === 'now' || value === 'later' ? value : null;
}

export function asPausedFrom(value: unknown): CampaignStatus | undefined {
  return value === 'scheduled' || value === 'sending' ? value : undefined;
}

export function asCount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

export function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function asIso(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

export function asStrings(value: unknown, max?: number): string[] {
  if (!Array.isArray(value)) return [];
  const list = value.filter((item): item is string => typeof item === 'string' && item.trim() !== '');
  return max ? list.slice(0, max) : list;
}

/** Campanha sem público (duplicata antiga, dado inconsistente): a tela mostra zeros, não quebra. */
export const EMPTY_AUDIENCE: AudienceSnapshot = {
  audienceId: '',
  audienceName: 'Sem público',
  total: 0,
  eligible: 0,
  excludedNoConsent: 0,
  excludedUnsubscribed: 0,
  excludedInvalid: 0,
};

export function mapAudienceSnapshot(audience: ApiAudienceSnapshot | null | undefined): AudienceSnapshot {
  if (!audience) return { ...EMPTY_AUDIENCE };
  return {
    audienceId: asText(audience.audienceId) || asText(audience.segmentationId),
    audienceName: asText(audience.audienceName) || asText(audience.name) || EMPTY_AUDIENCE.audienceName,
    total: asCount(audience.total),
    eligible: asCount(audience.eligible),
    excludedNoConsent: asCount(audience.excludedNoConsent),
    excludedUnsubscribed: asCount(audience.excludedUnsubscribed),
    excludedInvalid: asCount(audience.excludedInvalid),
  };
}

export function mapAudienceOption(option: ApiAudienceOption): AudienceSegment {
  return {
    id: asText(option.id) || asText(option.segmentationId),
    name: asText(option.name),
    description: asText(option.description),
    total: asCount(option.total),
    eligible: asCount(option.eligible),
    excludedNoConsent: asCount(option.excludedNoConsent),
    excludedUnsubscribed: asCount(option.excludedUnsubscribed),
    excludedInvalid: asCount(option.excludedInvalid),
  };
}

export function mapSendPoints(points: ApiSendPoint[] | undefined): SendPoint[] | undefined {
  if (!Array.isArray(points)) return undefined;
  return points.map((point) => ({
    label: asText(point.label),
    sent: asCount(point.sent),
    delivered: asCount(point.delivered),
  }));
}

export function mapSendErrors(errors: ApiSendError[] | undefined): SendErrorRow[] | undefined {
  if (!Array.isArray(errors)) return undefined;
  return errors.map((error, index) => ({
    id: asText(error.id) || `erro-${index}`,
    code: asText(error.code) || '—',
    message: asText(error.message),
    count: asCount(error.count),
  }));
}

export function mapRecipientsPage<ApiRow, Row>(
  page: ApiRecipientsPage<ApiRow> | undefined,
  mapRow: (row: ApiRow) => Row,
): RecipientsPage<Row> | undefined {
  if (!page || typeof page !== 'object' || !Array.isArray(page.rows)) return undefined;
  return {
    page: Math.max(1, asCount(page.page) || 1),
    pageSize: Math.max(1, asCount(page.pageSize) || page.rows.length || 1),
    total: asCount(page.total),
    rows: page.rows.map(mapRow),
  };
}
